<script setup lang="ts">
// ページ遷移時に NavigationGuard が行う認証確認（authedHello）が 401 以外（timeout/offline/5xx）で
// 失敗し、ログイン状態が未確立（is_loggedin=false）のまま requiredAuth ルートへ遷移しようとしたときに表示する画面。
import { onBeforeUnmount, ref } from 'vue'
import { useRouter } from 'vue-router'
import axios from 'axios'
import { useAuthStore } from '@/stores/authStore'

// props
interface Props {
  // 復帰後に戻る元の遷移先（in-app path）
  redirect?: string
}
const props = withDefaults(defineProps<Props>(), {
  redirect: '',
})

const router = useRouter()
const authStore = useAuthStore()

// redirect は自アプリ内パスのみ許可（open redirect 防止）。不正値は /chat にフォールバック
const target =
  props.redirect.startsWith('/') && !props.redirect.startsWith('//') ? props.redirect : '/chat'

const checking = ref(false)
const failed = ref(false)

function attempt() {
  if (checking.value) return // 初回・online・手動 Retry の競合で多重実行しない
  checking.value = true
  failed.value = false
  authStore
    .authedHello()
    .then(() => {
      router.replace(target)
    })
    .catch((err: unknown) => {
      const status = axios.isAxiosError(err) ? err.response?.status : undefined
      if (status === 401) {
        // api.ts の interceptor が logout + /login?authError=true へ遷移させるのでここでは何もしない
        return
      }
      checking.value = false
      failed.value = true
    })
}

function onOnline() {
  if (failed.value) attempt()
}
window.addEventListener('online', onOnline)
onBeforeUnmount(() => {
  window.removeEventListener('online', onOnline)
})

attempt()
</script>

<template>
  <div class="reconnect">
    <div class="panel">
      <div v-if="checking" class="spinner" role="status" aria-label="Reconnecting" />
      <h1 class="title">Reconnecting…</h1>
      <template v-if="failed">
        <p class="subtext">Can't reach the server. Tap Retry to reconnect.</p>
        <el-button class="retry" @click="attempt">Retry</el-button>
      </template>
    </div>
  </div>
</template>

<style scoped>
.reconnect {
  height: 100dvh;
  width: 100vw;
  overflow: hidden;
  background: linear-gradient(135deg, #1a1a2e 0%, #16213e 100%);
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 20px;
  box-sizing: border-box;
}
.panel {
  width: 100%;
  max-width: 360px;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 20px;
  text-align: center;
}
.spinner {
  width: 44px;
  height: 44px;
  border: 3px solid rgba(255, 255, 255, 0.25);
  border-top-color: #ffffff;
  border-radius: 50%;
  animation: spin 0.8s linear infinite;
}
@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}
.title {
  color: #ffffff;
  font-size: 22px;
  font-weight: bold;
  line-height: 30px;
  margin: 0;
}
.subtext {
  color: rgba(255, 255, 255, 0.75);
  font-size: 14px;
  line-height: 20px;
  margin: 0;
}
.retry {
  width: 200px;
  height: 44px;
  font-size: 16px;
  font-weight: 600;
  border-radius: 8px;
  background: #409eff;
  border: none;
  color: white;
  box-shadow: 0 4px 12px rgba(64, 158, 255, 0.3);
}
.retry:hover {
  background: #66b1ff;
}
</style>
